/**
 * Centralized Formatting Utilities
 * 
 * AUTOMATIC FORMATTING: These utilities ensure consistent formatting across
 * all 833+ collections and 1488+ products automatically.
 * 
 * Never needs manual updates - all new content inherits correct formatting.
 */

import { applyBrandRules } from '@/lib/brand-rules'

/**
 * Format a rating to exactly 1 decimal place with optional star
 * Handles null/undefined gracefully with sensible defaults
 * 
 * @example formatRating(4.966378769950212) → "4.9★"
 * @example formatRating(4.0, false) → "4.0"
 * @example formatRating(null) → "4.0★"
 */
export function formatRating(rating: number | null | undefined, showStar = true): string {
  const numRating = typeof rating === 'number' && !isNaN(rating) ? rating : 4.0
  const formatted = numRating.toFixed(1)
  return showStar ? `${formatted}★` : formatted
}

/**
 * Format collection name for display - ensures proper capitalization
 * Handles brand collections (LG Wine Coolers) and regular collections (Wine Coolers)
 * 
 * Note: Collection names from the API are already properly capitalized,
 * this function ensures consistency if called with lowercase input.
 * 
 * @example formatCollectionDisplay("wine coolers") → "Wine Coolers"
 * @example formatCollectionDisplay("LG Wine Coolers") → "LG Wine Coolers"
 */
export function formatCollectionDisplay(collectionName: string): string {
  if (!collectionName) return ''
  
  // Apply brand rules first for proper brand capitalization
  const withBrandRules = applyBrandRules(collectionName)
  
  return withBrandRules
}

/**
 * Get singular form of collection name while preserving capitalization
 * Handles common plural patterns
 * 
 * @example getCollectionSingular("Wine Coolers") → "Wine Cooler"
 * @example getCollectionSingular("Refrigerator Accessories") → "Refrigerator Accessory"
 */
export function getCollectionSingular(collectionName: string): string {
  if (!collectionName) return ''
  
  return collectionName
    .replace(/ies$/i, 'y')    // Accessories → Accessory
    .replace(/es$/i, '')      // Boxes → Box (careful with this one)
    .replace(/s$/i, '')       // Coolers → Cooler
}

/**
 * Format price for display with currency symbol.
 *
 * IMPORTANT: input is **dollars** (e.g., 1299.99), NOT cents.
 * For raw integer cents from the database (e.g., `price_cents = 13399`),
 * use {@link formatCents} instead — it divides by 100 first.
 *
 * Mixing the two is the bug class behind the 2026-05-07 incident where the
 * Avanti AR17T1B compact fridge rendered `$13,399` (raw cents read as dollars
 * with thousands separator) instead of `$134` (the actual price).
 *
 * DISPLAY RULE (owner ruling, 07/25/2026): a whole-dollar price renders with
 * NO decimals — never "$1,395.00", always "$1,395". A price carrying real
 * cents renders those cents EXACTLY — "$1,229.17" is never rounded or
 * truncated to "$1,229". The value itself decides; there is no caller
 * override. Sub-cent noise (float artifacts, statistical aggregates) is
 * snapped to the nearest cent before the whole-dollar check.
 *
 * DISPLAY ONLY — JSON-LD / schema price fields are numeric-style strings
 * ("1395.00") built elsewhere and are NOT routed through this helper.
 *
 * @param price dollars
 * @param _legacyShowCents retained so existing `formatPrice(x, true)` call
 *   sites keep compiling; it no longer changes the output — the ruling above
 *   is unconditional in both directions.
 *
 * @example formatPrice(1395)    → "$1,395"
 * @example formatPrice(1299.99) → "$1,299.99"
 * @example formatPrice(8.5)     → "$8.50"
 */
export function formatPrice(
  price: number | null | undefined,
  _legacyShowCents = false
): string {
  if (price === null || price === undefined || isNaN(price)) {
    return 'Price varies'
  }

  // Snap to the nearest cent first so 1394.9999999 (float noise) reads as
  // whole and 1345.6789 (aggregate artifact) reads as $1,345.68.
  const cents = Math.round(price * 100)
  const digits = cents % 100 === 0 ? 0 : 2

  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })
}

/**
 * Format raw integer cents (as stored in `retailer_offers.price_cents`,
 * `products.lowest_price_cents`, etc.) for display.
 *
 * Always prefer this over {@link formatPrice} when reading from the database
 * or from any code path where the price unit is "cents". It divides by 100
 * once, applies sanity-check guards, and formats consistently.
 *
 * Returns an empty string for null/undefined/0 cents — callers can fall back
 * to "Check price" / "Price varies" if they want a placeholder.
 *
 * DISPLAY RULE (owner ruling, 07/25/2026): whole-dollar values drop the
 * ".00"; real cents render exactly. `opts.showCents` is retained for call
 * sites that still pass it but no longer changes the output — see
 * {@link formatPrice}.
 *
 * @example formatCents(139500)  → "$1,395"      (whole dollars — no ".00")
 * @example formatCents(122917)  → "$1,229.17"   (real cents kept exactly)
 * @example formatCents(13399)   → "$133.99"     (compact fridge case)
 * @example formatCents(419900)  → "$4,199"
 * @example formatCents(0)       → ""
 * @example formatCents(null)    → ""
 */
export function formatCents(
  cents: number | null | undefined,
  _opts: { showCents?: boolean } = {}
): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return ''
  if (cents <= 0) return ''
  return formatPrice(cents / 100)
}

/**
 * Sanity guard for prices stored as integer cents.
 *
 * Returns a warning string if the value is implausible for the given product
 * type (e.g., a $30,000 mini-fridge is almost certainly a cents-as-dollars
 * import bug). Returns null when the value looks reasonable.
 *
 * Caller decides what to do with a warning — log, block, quarantine, render
 * a placeholder. Does not throw, by design.
 *
 * Thresholds are intentionally generous: a $20k full-size luxury fridge is
 * legitimate, a $200k one is not.
 */
export function sanityCheckPriceCents(
  cents: number | null | undefined,
  productType?: string | null
): string | null {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return null
  if (cents <= 0) return null

  // Universal upper bound — no single fridge/freezer/accessory legitimately
  // costs over $200k. Anything above this is almost certainly cents-as-dollars
  // (where 13399 was meant to be 134 dollars but got stored as 13399 dollars
  // = 1,339,900 cents).
  if (cents > 20_000_000) {
    return `price_cents=${cents} ($${(cents / 100).toLocaleString()}) exceeds universal $200k cap — likely cents-as-dollars import bug`
  }

  const t = (productType || '').toLowerCase()

  // Compact / mini / wine / beverage / ice maker — most retail under $5k.
  // Above $20k for a 1.7 cu ft mini-fridge means something is wrong.
  if (/(?:^|-)(?:compact|mini|wine|beverage|kegerator|ice-?maker|ice machine)(?:$|-|s)/.test(t)) {
    if (cents > 2_000_000) {
      return `price_cents=${cents} ($${(cents / 100).toLocaleString()}) exceeds $20k cap for ${t} — likely 100x error`
    }
  }

  // Full-size refrigerator/freezer below $50 is almost certainly broken
  // (real fridges start around $300; a $5 fridge is a ghost row).
  if (/(?:french-door|side-by-side|top-freezer|bottom-freezer|counter-depth|refrigerator|freezer)/.test(t)) {
    if (cents < 5_000) {
      return `price_cents=${cents} ($${(cents / 100).toFixed(2)}) below $50 floor for ${t} — likely missing data or 100x-too-low error`
    }
  }

  return null
}

/**
 * Compare two prices stored as cents and return a warning if they differ
 * by an order of magnitude (10x or 100x). Used as a runtime cross-check
 * between the visible offer module and the SEO/citation content block.
 *
 * Both inputs are integer cents.
 */
export function detectPriceMismatch(
  visibleCents: number | null | undefined,
  generatedCents: number | null | undefined
): string | null {
  if (!visibleCents || !generatedCents) return null
  if (visibleCents <= 0 || generatedCents <= 0) return null
  const ratio = Math.max(visibleCents, generatedCents) / Math.min(visibleCents, generatedCents)
  if (ratio >= 10) {
    return `price ratio ${ratio.toFixed(1)}x between visible (${visibleCents}c) and generated (${generatedCents}c) — likely cents/dollars confusion`
  }
  return null
}

/**
 * Format review count for display
 * 
 * @example formatReviewCount(1234) → "1,234"
 * @example formatReviewCount(null) → ""
 */
export function formatReviewCount(reviews: number | null | undefined): string {
  if (reviews === null || reviews === undefined) return ''
  return reviews.toLocaleString()
}

/**
 * FRIDGE-590 (owner rule, 08/27/2026): every rendered integer count carries
 * the thousands separator — "1,000 locations", never "1000 locations". One
 * formatter so no emitter hand-rolls it; en-US locale pinned so server and
 * client render identically regardless of host locale. JSON-LD numeric fields
 * stay bare numbers by spec — this is for COPY only.
 *
 * @example formatCount(1000) → "1,000"
 * @example formatCount(31)   → "31"
 */
export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return ''
  return n.toLocaleString('en-US')
}

/**
 * Format capacity in cubic feet
 * 
 * @example formatCapacity(22.5) → "22.5 cu. ft."
 * @example formatCapacity(22) → "22.0 cu. ft."
 */
export function formatCapacity(capacity: number | null | undefined): string {
  if (capacity === null || capacity === undefined || isNaN(capacity)) {
    return ''
  }
  return `${capacity.toFixed(1)} cu. ft.`
}

/**
 * Truncate a numeric spec value to at most `maxDecimals` digits after the
 * decimal point WITHOUT rounding, then drop any trailing zeros.
 *
 * Owner spec (07/20/2026): specs display at most two decimals, truncated —
 *   36.99999996226 → "36.99"   (never 37 — truncate, don't round)
 *   86.19685030578 → "86.19"
 *   24.9999999745  → "24.99"
 *   29.88          → "29.88"   (already clean, unchanged)
 *   29.90          → "29.9"    (honest trailing-zero trim)
 *
 * IEEE-754 note: a literal like 29.9 becomes 2989.9999999999995 once scaled by
 * 100, so a naive Math.trunc(v * 100) / 100 would yield 29.89. We snap to the
 * nearest unit first when the scaled value sits within 1e-9 of an integer —
 * that only ever catches representation noise (~1e-13 for spec-sized values),
 * never a real fractional part (the tightest genuine case, 24.9999999745, is
 * 2.5e-6 away and still truncates down to 24.99).
 *
 * Render-time only — never mutates stored data.
 *
 * @example truncateSpecNumber(36.99999996226) → "36.99"
 */
export function truncateSpecNumber(value: number, maxDecimals = 2): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return ''
  const factor = 10 ** maxDecimals
  const scaled = value * factor
  const nearest = Math.round(scaled)
  const corrected = Math.abs(scaled - nearest) < 1e-9 ? nearest : Math.trunc(scaled)
  // Divide back and let JS pick the shortest round-trip string — that trims
  // trailing zeros for free (2990/100 → "29.9", 3700/100 → "37").
  return String(corrected / factor)
}

/**
 * Format an arbitrary product-spec value for public display. Real numbers and
 * numeric text are truncated to at most `maxDecimals` decimals via
 * {@link truncateSpecNumber}; any decimal number embedded in a longer string
 * (e.g. "36.99999996226 in") is truncated in place while its surrounding units
 * and separators are left intact. Booleans render as Yes/No; everything else
 * passes through unchanged. Integers (no decimal point) are never touched.
 *
 * This is the single wire-in point for every spec render path (PDP spec
 * tables, comparison tables, product cards, blog spec tables). Render-time
 * only — never mutates stored data.
 *
 * @example formatSpecValue(24.9999999745)        → "24.99"
 * @example formatSpecValue("86.19685030578 in")  → "86.19 in"
 * @example formatSpecValue("Stainless Steel")    → "Stainless Steel"
 */
export function formatSpecValue(value: unknown, maxDecimals = 2): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return truncateSpecNumber(value, maxDecimals)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  const str = String(value)
  // Truncate every decimal number that appears inside the string, leaving the
  // surrounding text intact. Integers (no decimal point) are left alone.
  return str.replace(/-?\d+\.\d+/g, (m) => truncateSpecNumber(Number(m), maxDecimals))
}

/**
 * Format location slug to proper title case
 * Used as fallback when database location_name is not available
 *
 * @example formatLocationName("new-york-city-ny") → "New York City"
 * @example formatLocationName("san-francisco-ca") → "San Francisco"
 * @example formatLocationName("arlington-va") → "Arlington"
 */
export function formatLocationName(slug: string): string {
  if (!slug) return ''
  
  // Remove trailing state code (e.g., "-ny", "-ca")
  const withoutState = slug.replace(/-[a-z]{2}$/i, '')
  
  // Replace hyphens with spaces and title case each word
  return withoutState
    .replace(/-/g, ' ')
    .replace(/\b\w+/g, word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
}